// src/App.js - Version complète avec gestion des utilisateurs

import React, { useState, useEffect } from 'react';
import { AlertCircle, Eye, CheckCircle, XCircle } from 'lucide-react';
import './App.css';

import LoginForm from './components/LoginForm';
import Header from './components/Header';
import StatsCards from './components/StatsCards';
import FiltersBar from './components/FiltersBar';
import ShopList from './components/ShopList';
import ShopDetails from './components/ShopDetails';
import ArticlesManager from './components/ArticlesManager';
import LeaderboardManager from './components/LeaderboardManager';
import UsersManager from './components/UsersManager';
import UserProfile from './components/UserProfile';
import { exportToCSV, parseCSV } from './utils/exportUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const BackofficeApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);

  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [filter, setFilter] = useState('review');
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, scrapped: 0, review: 0, validated: 0 });
  const [activeTab, setActiveTab] = useState('shops');
  const [showUserProfile, setShowUserProfile] = useState(false);

  const statusConfig = {
    scrapped: { label: 'Scrapé', color: 'bg-gray-500', icon: AlertCircle },
    review: { label: 'En révision', color: 'bg-orange-500', icon: Eye },
    validated: { label: 'Validé', color: 'bg-green-500', icon: CheckCircle },
    rejected: { label: 'Rejeté', color: 'bg-red-500', icon: XCircle }
  };

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (storedToken && user) {
      setToken(storedToken);
      setIsAuthenticated(true);
      setCurrentUser(JSON.parse(user));
    }
  }, []);

  // Permissions basées sur les rôles
  const hasPermission = (action) => {
    if (!currentUser) return false;
    
    const permissions = {
      admin: ['read', 'edit', 'validate', 'delete'],
      editor: ['read', 'edit', 'validate'],
      reader: ['read']
    };
    
    return permissions[currentUser.role]?.includes(action);
  };

  const handleDeleteShop = async (shopId) => {
    if (!hasPermission('delete')) {
      alert('Vous n\'avez pas la permission de supprimer des boutiques');
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/shops/${shopId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'rejected'
        })
      });
      
      if (response.ok) {
        await loadShops();
        await loadStats();
        if (selectedShop?._id === shopId) {
          setSelectedShop(null);
        }
        alert('✅ Boutique marquée comme rejetée');
      }
    } catch (error) {
      console.error('Error deleting shop:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  // Helper pour les requêtes authentifiées
  const authenticatedFetch = async (url, options = {}) => {
    const currentToken = token || localStorage.getItem('token');

    if (!currentToken) {
      handleLogout();
      throw new Error('Token manquant');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${currentToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 401 || response.status === 403) {
      handleLogout();
      throw new Error('Session expirée');
    }

    return response;
  };

  // Connexion
  const handleLogin = (newToken, user) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(user));
    setToken(newToken);
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  // Déconnexion
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setIsAuthenticated(false);
    setCurrentUser(null);
    setShops([]);
    setSelectedShop(null);
    setShowUserProfile(false);
  };

  // Charger les statistiques
  const loadStats = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/shops/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Charger les boutiques
  const loadShops = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await authenticatedFetch(`${API_URL}/shops?${params}`);
      const data = await response.json();
      setShops(data.shops);
    } catch (error) {
      console.error('Error loading shops:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && token && activeTab === 'shops') {
      loadStats();
      loadShops();
    }
  }, [filter, searchTerm, isAuthenticated, token, activeTab]);

  const handleRefresh = () => {
    if (activeTab === 'shops') {
      loadShops();
      loadStats();
    }
  };

  const handleValidate = async (shopId, approved) => {
    if (!hasPermission('validate')) {
      alert('Vous n\'avez pas la permission de valider des boutiques');
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/shops/${shopId}/validate`, {
        method: 'PATCH',
        body: JSON.stringify({
          approved,
          validatedBy: currentUser.email,
          reason: approved ? null : 'Refusé par le modérateur'
        })
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedShop(updated);
        await loadShops();
        await loadStats();
      }
    } catch (error) {
      console.error('Error validating shop:', error);
    }
  };

  const handleQuickValidate = async (shopId) => {
    if (!hasPermission('validate')) {
      alert('Vous n\'avez pas la permission de valider des boutiques');
      return;
    }

    if (!window.confirm('Valider cette boutique directement sans passer par la révision ?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_URL}/shops/${shopId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'validated'
        })
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSelectedShop(updated);
        await loadShops();
        await loadStats();
      }
    } catch (error) {
      console.error('Error validating shop:', error);
    }
  };

  // Ouvrir le modal d'édition depuis la liste
  const handleEditShop = (shop) => {
    if (!hasPermission('edit')) {
      alert('Vous n\'avez pas la permission d\'éditer des boutiques');
      return;
    }
    setSelectedShop(shop);
    setEditMode(true);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('fr-FR');
  };

  // Export
  const handleExport = () => {
    try {
      exportToCSV(shops);
      alert('Export réussi !');
    } catch (error) {
      console.error('Export error:', error);
      alert('Erreur lors de l\'export');
    }
  };

  // Import
  const handleImport = async (file) => {
    try {
      const shops = await parseCSV(file);
      
      if (!window.confirm(`Importer ${shops.length} boutique(s) ?`)) {
        return;
      }

      const response = await authenticatedFetch(`${API_URL}/shops/batch`, {
        method: 'POST',
        body: JSON.stringify({ shops })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Import réussi !\n✅ ${result.success.length} créées\n⚠️ ${result.duplicates.length} doublons\n❌ ${result.errors.length} erreurs`);
        await loadShops();
        await loadStats();
      } else {
        throw new Error('Erreur lors de l\'import');
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Erreur lors de l\'import: ' + error.message);
    }
  };

  // Écran de connexion
  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} apiUrl={API_URL} />;
  }

  // Modal profil utilisateur
  if (showUserProfile) {
    return (
      <div className="app">
        <div className="header">
          <div className="header-content">
            <div>
              <h1>Mon Profil</h1>
              <p className="subtitle">Gérez vos informations personnelles</p>
            </div>
            <div className="header-actions">
              <button 
                onClick={() => setShowUserProfile(false)} 
                className="btn-secondary"
              >
                ← Retour au backoffice
              </button>
            </div>
          </div>
        </div>
        <div className="container" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
          <UserProfile
            currentUser={currentUser}
            apiUrl={API_URL}
            token={token}
            onLogout={handleLogout}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header 
        currentUser={currentUser} 
        onRefresh={handleRefresh} 
        onLogout={handleLogout}
        onProfileClick={() => setShowUserProfile(true)}
      />

      {/* Navigation par onglets */}
      <div className="container">
        <div className="tabs-navigation">
          <button
            onClick={() => setActiveTab('shops')}
            className={`tab-btn ${activeTab === 'shops' ? 'active' : ''}`}
          >
            🏪 Boutiques
          </button>
          <button
            onClick={() => setActiveTab('articles')}
            className={`tab-btn ${activeTab === 'articles' ? 'active' : ''}`}
          >
            📰 Actualités
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`tab-btn ${activeTab === 'leaderboard' ? 'active' : ''}`}
          >
            🏆 Classement
          </button>
          {currentUser.role === 'admin' && (
            <button
              onClick={() => setActiveTab('users')}
              className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            >
              👥 Utilisateurs
            </button>
          )}
        </div>
      </div>

      {/* Contenu selon l'onglet actif */}
      {activeTab === 'shops' ? (
        <>
          <StatsCards stats={stats} />
          <FiltersBar
            filter={filter}
            setFilter={setFilter}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusConfig={statusConfig}
            onExport={handleExport}
            onImport={handleImport}
          />

          <div className="container">
            <div className="main-grid">
              <ShopList
                stats={stats}
                shops={shops}
                selectedShop={selectedShop}
                onSelectShop={setSelectedShop}
                onEditShop={handleEditShop}
                onDeleteShop={handleDeleteShop}
                hasEditPermission={hasPermission('edit')}
                hasDeletePermission={hasPermission('delete')}
                loading={loading}
                statusConfig={statusConfig}
              />
              <ShopDetails
                shop={selectedShop}
                hasPermission={hasPermission}
                editMode={editMode}
                onEditModeChange={setEditMode}
                onValidate={handleValidate}
                onQuickValidate={handleQuickValidate}
                formatDate={formatDate}
                apiUrl={API_URL}
                token={token}
              />
            </div>
          </div>
        </>
      ) : activeTab === 'leaderboard' ? (
        <div className="container">
          <LeaderboardManager
            apiUrl={API_URL}
            currentUser={currentUser}
            token={token}
          />
        </div>
      ) : activeTab === 'users' ? (
        <div className="container">
          <UsersManager
            apiUrl={API_URL}
            token={token}
            currentUser={currentUser}
          />
        </div>
      ) : (
        <div className="container">
          <ArticlesManager
            apiUrl={API_URL}
            token={token}
          />
        </div>
      )}
    </div>
  );
};

export default BackofficeApp;